import { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import useNotification from "../../hooks/notification.hook";
import ModalContainer from "./ModalContainer";
import {
	createTemplatesPlansContentPresets,
	editTemplatesPlansContentPresets,
} from "../../services/plans-template-content-preset.service";
import { cn } from "../../lib/utils";
import SingleSelect from "../singleselect/SingleSelect";

export default function CrudPlanContentPresetsPopup({
	open,
	setOpen,
	planData,
	contentPresetsModel,
	presetDefaults = null,
	organizationTypes,
	contentPresetsGrouped = {},
	isLoadingContentPresets = false,
	onEdit = () => {},
	onInsert = () => {},
}) {
	// Presets raggruppati per tipologia organizzazione: { [key]: { id, title, listItems } }
	const contentPresetsCount = Object.values(contentPresetsGrouped).reduce(
		(acc, group) => acc + (group?.listItems?.length ?? 0),
		0
	);

	// Schema Yup
	const schema = yup.object().shape({
		title: yup.string().required(),
		description: yup.string().optional(),
		organization_type_id: yup.string().required(),
		active: yup.bool().optional(),
	});

	const {
		register,
		setValue,
		clearErrors,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function formHandler(data) {
		const body = {
			templatePlan: planData.id,
			...data,
			...(selectedItem?.id && { source_preset_id: selectedItem?.id }),
		};

		if (!loading) {
			setLoading(true);

			if (!contentPresetsModel?.id) {
				// Create plan
				createTemplatesPlansContentPresets(
					body,
					(res) => {
						setLoading(false);
						openNotification({
							text: "Preset creato con successo!",
							status: "success",
						});
						onInsert(res, selectedItem?.id ?? null);
					},
					(error) => {
						helperErrorNotification(error);
						setLoading(false);
					}
				);
			} else {
				// Edit plan
				editTemplatesPlansContentPresets(
					{ contentPreset: contentPresetsModel.id, ...body },
					(res) => {
						setLoading(false);
						openNotification({
							text: "Preset modificato con successo!",
							status: "success",
						});
						onEdit(res);
					},
					(error) => {
						helperErrorNotification(error);
						setLoading(false);
					}
				);
			}
		}
	}

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	/**
	 * Init value
	 * In creation, `presetDefaults` prefills the form (es. duplicazione preset)
	 */
	useEffect(() => {
		if (open) {
			const model = contentPresetsModel?.id ? contentPresetsModel : presetDefaults;

			clearErrors();
			setValue("title", model?.title ? model.title : "");
			setValue("description", model?.description ? model.description : "");
			setValue(
				"organization_type_id",
				model?.organization_type_id ? model.organization_type_id : ""
			);
			setValue("active", model?.active ?? true);
			setSelectedItem(contentPresetsModel?.id ? null : (presetDefaults?.sourcePreset ?? null));
		}
	}, [open]);

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen}>
			<div
				className={"main_modal_content medium_size_modal"}
				id={"crud-plan-content-presets-popup"}
			>
				<button
					className="main_close"
					onClick={() => setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				<div className="main_header_modal left_text">
					<span className="title">
						{contentPresetsModel?.id
							? "Modifica preset"
							: presetDefaults?.sourcePreset
								? "Duplica preset"
								: "Nuovo preset"}
					</span>
				</div>

				{/* Modal Form */}
				<div className={"main_body_modal"}>
					<form
						id="crud-template-plan-popup"
						className="global_form_landing"
						onSubmit={handleSubmit(formHandler)}
						noValidate
					>
						<div className="base child">
							{/* Title */}
							<div className="child">
								<div className="wrap_input_landing">
									<label htmlFor="crud_plan_content_presets_name" className="label_input">
										Titolo
									</label>
									<input
										className={`landing big${errors.title ? " err" : ""}`}
										id="crud_plan_content_presets_content_presets_title"
										type="text"
										{...register("title")}
									/>
								</div>
							</div>
						</div>

						{/* Description */}
						<div className="base">
							<div className="wrap_input_landing">
								<label htmlFor="crud_plan_content_presets_description" className="label_input">
									Descrizione
								</label>
								<input
									className={`landing big${errors.description ? " err" : ""}`}
									id="crud_plan_content_presets_description"
									type="text"
									{...register("description")}
								/>
							</div>
						</div>

						{/* organization_type_id */}
						<div className="base">
							<div className="main_select_landing">
								<label htmlFor="crud_plan_content_presets_organization_id" className="label_input">
									Tipologia organizzazione
								</label>
								<div className="relative">
									<select
										{...register("organization_type_id")}
										id="crud-crud_plan_content_presets_organization_id"
										className={cn("landing !min-h-[40px]", errors.assigned_to && "err")}
										disabled={organizationTypes.length === 0}
									>
										{organizationTypes.map((organizationType) => (
											<option value={organizationType.id} key={organizationType.id}>
												{organizationType.name}
											</option>
										))}
									</select>
									<div className="wrap_arrow">
										<span className={cn("icon_16 default chevron icon_mask !bg-interactive-100")} />
									</div>
								</div>
							</div>
						</div>

						{/* Single select item */}
						{!contentPresetsModel?.id && (
							<div className="base">
								<div className="wrap_input_landing">
									<span className="label_input">Preset di partenza</span>
									<SingleSelect
										listItems={contentPresetsGrouped}
										selectedItem={selectedItem}
										onSelect={setSelectedItem}
										placeholder={"Seleziona preset"}
										isLoading={isLoadingContentPresets}
										searchable={contentPresetsCount > 5}
										disabled={!isLoadingContentPresets && !contentPresetsCount}
									/>
								</div>
							</div>
						)}

						{/* active */}
						<div className="base pt-2">
							<div className="toggle_landing">
								<input
									type="checkbox"
									id="crud-plan-active"
									className="landing"
									{...register("active")}
								/>
								<label htmlFor="crud-plan-active">Abilita preset</label>
							</div>
						</div>
					</form>
				</div>

				<div className="main_footer_modal">
					<div className="right_content">
						{/* Main Cta */}

						<button
							form={"crud-template-plan-popup"}
							className={`button fill success${loading ? " loading" : ""}`}
							type="submit"
						>
							<div className="load_content">Conferma</div>
							<div className="main_loader_icon fade_in"></div>
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
}
